import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Read a usable JWT from React state or localStorage (never stale undefined/null strings). */
export function getSessionToken(storedToken) {
  const candidate = storedToken || localStorage.getItem('docmania_token');
  if (!candidate || candidate === 'undefined' || candidate === 'null') {
    return null;
  }
  return candidate;
}

export function isSessionTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload?.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

export function getAuthHeaders(storedToken) {
  const sessionToken = getSessionToken(storedToken);
  if (!sessionToken || isSessionTokenExpired(sessionToken)) {
    return {};
  }
  return { Authorization: `Bearer ${sessionToken}` };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('docmania_token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: getAuthHeaders(token),
  });

  const clearSession = () => {
    localStorage.removeItem('docmania_token');
    delete api.defaults.headers.common.Authorization;
    setToken(null);
    setUser(null);
  };

  // Intercept 429 rate-limit responses with a user-friendly toast
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const detail = error.response.data?.detail;
        if (detail === 'Invalid token' || detail === 'User not found') {
          toast.error('Your session has expired. Please sign in again.', { id: 'auth-expired-401' });
          clearSession();
        }
      }
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers?.['retry-after'];
        const detail = error.response.data?.detail || 'Too many requests. Please slow down.';
        const retryMsg = retryAfter ? ` Try again in ${retryAfter}s.` : '';
        toast.error(`${detail}${retryMsg}`, { id: 'rate-limit-429' });
      }
      // Network error (no response = offline or server unreachable)
      if (!error.response && error.code === 'ERR_NETWORK') {
        toast.error('Unable to reach the server. Check your connection and try again.', { id: 'network-error' });
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const sessionToken = getSessionToken(token);
    if (sessionToken) {
      api.defaults.headers.common.Authorization = `Bearer ${sessionToken}`;
      fetchUser();
    } else {
      delete api.defaults.headers.common.Authorization;
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // If offline or network error, keep existing cached user state from token
      if (!error.response) {
        console.warn('Network error fetching user, keeping cached session');
        const sessionToken = getSessionToken(token);
        if (!sessionToken || isSessionTokenExpired(sessionToken)) {
          clearSession();
          return;
        }
        try {
          const payload = JSON.parse(atob(sessionToken.split('.')[1]));
          setUser(prev => prev || { id: payload.sub, role: payload.role, _offline: true });
        } catch {
          clearSession();
        }
      } else {
        // Server responded with error (401, etc.) — clear session
        clearSession();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('docmania_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, full_name, country) => {
    const response = await api.post('/auth/register', { email, password, full_name, country });
    return response.data;
  };

  const logout = () => {
    clearSession();
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isReviewer = user?.role === 'reviewer';
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser,
      isAdmin,
      isReviewer,
      isSuperAdmin,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};


